import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import _ from 'lodash'
import {
    Button,
    Paper,
    TextField,
    Typography,
    FormControl,
    InputLabel,
    Select,
    OutlinedInput,
    MenuItem,
    Slider,
    Icon,
    IconButton,
    Collapse,
    FormControlLabel,
    Checkbox,
    Backdrop,
} from '@material-ui/core'
import { useIntl } from 'src/common/razzle-platform-translate'
import './moreFilter.css'
import { CustomSwitch } from 'src/common/razzle-platform-map/components/CustomSwitch'

const minDistance = 10

function MoreFilter(props) {
    const [open, setOpen] = useState([])
    const { formatMessage } = useIntl()
    const {
        handleChange,
        values: outsideFilters,
        clearFilter,
        save,
        specificities,
        setFilter: setOutsideFilters,
        close,
    } = props
    const [showClear, setShowClear] = useState(true)
    const [searchValue, setSearchValue] = useState(outsideFilters.search || '')
    const [insideFilters, setInsideFilters] = useState(outsideFilters ? { ...outsideFilters } : {})
    // insideFilters used to only store internal filters values so when we close the Backdrop without saving, the filters won't apply.  
    const [priceValue, setPriceValue] = React.useState([(outsideFilters.gePrice || 0), (outsideFilters.lePrice || 1000)])
    let isEmpty = useRef(0)
    // isEmpty used to store any changes happen within a filter so we can know if the filters are empty or not, to show or hide the clear button
    useEffect(() => {
        const newFilters = {};
        ;[...getSpecKeys(specificities)].forEach((key) => {
            if (key.includes('boolchoice_') && outsideFilters[key] !== undefined) {
                if (typeof outsideFilters[key] === 'string') {
                    newFilters[key] = outsideFilters[key] === 'true' ? true : false
                } else {
                    newFilters[key] = outsideFilters[key]
                }
                isEmpty.current += 1
            } else if (outsideFilters[key]?.length) {
                newFilters[key] = outsideFilters[key]
                isEmpty.current += typeof outsideFilters[key] === 'string' ? 1 : outsideFilters[key].length
            }
        })
        setInsideFilters(newFilters)
        setShowClear(!isEmpty.current && !searchValue && !outsideFilters.gePrice && !outsideFilters.lePrice)
    }, [])

    useEffect(() => {
        setShowClear(!searchValue  && !+priceValue[0] && +priceValue[1] === 1000 && !isEmpty.current)
    }, [searchValue, isEmpty.current, priceValue])

    useEffect(() => {
        const parent = document.querySelector('.products-map-container')
        parent.style.zIndex = 100
        return () => {
            parent.style.zIndex = 0
        }
    }, [])

    const handlePriceChange = (event, newValue) => {
        if (!Array.isArray(newValue)) {
            return
        }
        if (newValue[0] < 0) {
            newValue[0] = 0
        }
        if (newValue[1] > 1000) {
            newValue[1] = 1000
        }
        if (newValue[1] - newValue[0] < minDistance) {
                const clamped = Math.max(newValue[1], minDistance)
                setPriceValue([clamped - minDistance, clamped])
        } else {
            setPriceValue(newValue)
        }
    }    

    const handleSearchChange = (event) => {
        const {value} = event.target
        setSearchValue(value)
    }
    

    const handleFilterChange = (event) => {
        const { name, value } = event.target
        const currentFilterValues = insideFilters[name]
        let nextFilterValues = { ...insideFilters }

        if (currentFilterValues !== undefined) {
            if (currentFilterValues.includes(value)) {
                nextFilterValues[name] = Array.isArray(currentFilterValues)
                    ? currentFilterValues.filter((val) => val !== value)
                    : []
                isEmpty.current -= 1
            } else {
                isEmpty.current += 1
                Array.isArray(nextFilterValues[name])
                    ? nextFilterValues[name].push(value)
                    : (nextFilterValues[name] = [nextFilterValues[name], value])
            }
        } else {
            isEmpty.current += 1
            nextFilterValues[name] = [value]
        }
        setInsideFilters({ ...nextFilterValues })
    }

    const handleFilterBooleanChange = (event) => {
        const { name, checked } = event.target
        const currentFilterValues = insideFilters[name]
        let nextFilterValues = { ...insideFilters }
        if (currentFilterValues === undefined) {
            isEmpty.current += 1
        }
        nextFilterValues[name] = checked
        setInsideFilters({ ...nextFilterValues })
    }

    const saveFilters = () => {
        const newOutsideFilters = {
            gePrice: priceValue[0] ? priceValue[0] : undefined,
            lePrice: priceValue[1] && priceValue[1] !== 1000 ? priceValue[1] : undefined,
            search: searchValue || undefined,
            ...insideFilters
        }
        setOutsideFilters({ ...newOutsideFilters }, false, true)
        close()
    }

    const clearAll = () => {
        clearFilter(['search', 'gePrice', 'lePrice', ...getSpecKeys(specificities)])
        setInsideFilters({})
        setPriceValue([0, 1000])
        setShowClear(true)
        setSearchValue('')
        isEmpty.current = 0
    }

    const getSpecKeys = (specificities) => {
        let keys = []
        specificities.forEach((spec) => {
            if (spec.type === 'date') {
                keys.push(`productSpecificities.date_${spec.id}`)
            } else if (spec.type === 'm_list') {
                keys.push(`productSpecificities.choices_${spec.id}`)
            } else if (spec.type === "boolean") {
                keys.push(`productSpecificities.boolchoice_${spec.id}`)
            } else {
                keys.push(`productSpecificities.choice_${spec.id}`)
            }
        })

        return keys
    }

    const checkChoiceChecked = (key, id) => {
        return insideFilters[key]?.includes(id) ? true : false
    }

    const handleCollapse = (id) => {
        const newOpen = checkCollapsed(id) ? open.filter((o) => o !== id) : [...open, id]
        setOpen(newOpen)
    }

    const checkCollapsed = (id) => open.includes(id)

    const sortedSpecificities = useMemo(() => {
        let newSpecificities = []
        specificities?.forEach((specificity) => {
            specificity.choices ? newSpecificities.push(specificity) : newSpecificities.unshift(specificity)
        })
        return newSpecificities
    }, [])

    const sortAlphabetically = useCallback((a, b) => {
        if (a.label.toLowerCase() < b.label.toLowerCase()) {
            return -1
        }
        if (a.label.toLowerCase() > b.label.toLowerCase()) {
            return 1
        }
        return 0
    }, [])

    const renderChoices = useCallback(
        (choices = [], specType, specId) => {
            return choices.sort(sortAlphabetically).map(({ label, value }) => {
                const isChecked = checkChoiceChecked(`productSpecificities.${specType}_${specId}`, value)
                return (
                    <div key={label} className="checkbox">
                        <div>
                            <FormControlLabel
                                key={label}
                                control={
                                    <Checkbox
                                        name={`productSpecificities.${specType}_${specId}`}
                                        value={value}
                                        onChange={(event) => handleFilterChange(event)}
                                        checked={isChecked}
                                        className={`mr-4 ${isChecked ? 'checked' : 'not-checked'}`}
                                    />
                                }
                                label={label}
                            />
                        </div>
                    </div>
                )
            })
        },
        [insideFilters],
    )

    return (
        <Backdrop open={true} onClick={close} style={{ zIndex: 10000000 }}>
            <div className="parent">
                <div className="popup" onClick={(e) => e.stopPropagation()}>
                    <div className="close" onClick={close}>
                        <div>
                            <i className="fas fa-times"></i>
                        </div>
                    </div>
                    <div className="header">
                        <h1>{formatMessage({ id: 'Other Filters', defaultMessage: 'Other Filters' })}</h1>
                    </div>
                    <div className="main">
                        <div className="filter">
                            <TextField
                                label={formatMessage({ id: 'Search', defaultMessage: 'Search' })}
                                type="text"
                                name="search"
                                value={searchValue}
                                onChange={handleSearchChange}
                                variant="outlined"
                                fullWidth
                            />
                        </div>
                        <div className="filter">
                            <legend className="filter-title">
                                <h2>{formatMessage({ id: 'Price Range', defaultMessage: 'Price Range' })}</h2>
                                <h6>
                                    ({priceValue[0]}
                                    {' - '}
                                    {priceValue[1] === 1000 ? priceValue[1] + '+' : priceValue[1]})
                                </h6>
                            </legend>
                            <Slider
                                name="Price Filter"
                                value={priceValue}
                                onChange={handlePriceChange}
                                valueLabelDisplay="auto"
                                disableSwap
                                max={1000}
                            />
                            <div className="filter-content">
                                <div className="price-wrapper">
                                    <TextField
                                        label={formatMessage({ id: 'Min', defaultMessage: 'Min' })}
                                        name="gePrice"
                                        type="number"
                                        variant="outlined"
                                        onChange={(event) =>
                                            handlePriceChange(event, [event.target.value || 0, priceValue[1]])
                                        }
                                        value={priceValue[0]}
                                    />
                                    <TextField
                                        label={formatMessage({ id: 'Max', defaultMessage: 'Max' })}
                                        name="lePrice"
                                        type="number"
                                        variant="outlined"
                                        onChange={(event) =>
                                            handlePriceChange(event, [priceValue[0], event.target.value])
                                        }
                                        value={priceValue[1]}
                                    />
                                </div>
                            </div>
                        </div>
                        {sortedSpecificities.length > 0 ? (
                            sortedSpecificities.map((spec) => {
                                const specType =
                                    spec.type === 'm_list'
                                        ? 'choices'
                                        : spec.type === 'date'
                                        ? 'date'
                                        : spec.type === 'boolean'
                                        ? 'boolchoice'
                                        : 'choice'

                                const isCollapsed = checkCollapsed(spec.id)

                                if (['str', 'free'].includes(spec.type)) {
                                    return null
                                }

                                if (spec.type === 'date') {
                                    return (
                                        <div className="filter">
                                            <TextField
                                                key={spec.id}
                                                label={spec.name}
                                                type="date"
                                                name={`productSpecificities.${specType}_${spec.id}`}
                                                value={
                                                    outsideFilters[`productSpecificities.${specType}_${spec.id}`] || ''
                                                }
                                                onChange={handleChange}
                                                variant="outlined"
                                                InputLabelProps={{ shrink: true }}
                                                fullWidth
                                            />
                                        </div>
                                    )
                                } else if (spec.type === 'boolean') {
                                    return (
                                        <div className="filter py-4">
                                            <legend className="filter-title mb-0">
                                                <CustomSwitch
                                                    label={
                                                        <>
                                                            <h2>{spec.name}</h2>
                                                            <h6
                                                                style={{
                                                                    opacity:
                                                                        insideFilters[
                                                                            `productSpecificities.${specType}_${spec.id}`
                                                                        ] !== undefined
                                                                            ? '1'
                                                                            : '0',
                                                                }}
                                                            >
                                                                (+)
                                                            </h6>
                                                        </>
                                                    }
                                                    name={`productSpecificities.${specType}_${spec.id}`}
                                                    checked={
                                                        !!insideFilters[`productSpecificities.${specType}_${spec.id}`]
                                                    }
                                                    description={spec.description}
                                                    handleChange={handleFilterBooleanChange}
                                                />
                                            </legend>
                                        </div>
                                    )
                                }
                                return (
                                    <div className="filter" key={spec.id}>
                                        <legend className="filter-title">
                                            <h2>{spec.name}</h2>
                                            <h6
                                                style={{
                                                    opacity: !!insideFilters[
                                                        `productSpecificities.${specType}_${spec.id}`
                                                    ]?.length
                                                        ? '1'
                                                        : '0',
                                                }}
                                            >
                                                ({insideFilters[`productSpecificities.${specType}_${spec.id}`]?.length}{' '}
                                                {formatMessage({ id: 'selected', defaultMessage: 'selected' })})
                                            </h6>
                                        </legend>
                                        <div className="filter-content">
                                            {renderChoices(spec.choices, specType, spec.id).filter(
                                                (choice, idx) => idx < 4,
                                            )}
                                            <Collapse in={isCollapsed} timeout="auto" unmountOnExit>
                                                <div className="filter-content">
                                                    {renderChoices(spec.choices, specType, spec.id)?.filter(
                                                        (choice, idx) => idx > 3,
                                                    )}
                                                </div>
                                            </Collapse>
                                        </div>
                                        {spec.choices?.length > 4 && (
                                            <div className="show-more" onClick={() => handleCollapse(spec.id)}>
                                                <div>
                                                    <button>
                                                        {formatMessage({
                                                            id: isCollapsed ? 'Show less' : 'Show all',
                                                            defaultMessage: isCollapsed ? 'Show less' : 'Show all',
                                                        })}
                                                    </button>
                                                    <IconButton className="p-0">
                                                        <Icon>
                                                            {isCollapsed ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                                                        </Icon>
                                                    </IconButton>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )
                            })
                        ) : (
                            <h1 className="no-filter-message">
                                {formatMessage({ id: 'Please first select any category to see its filters', defaultMessage: 'Please first select any category to see its filters' })}
                            </h1>
                        )}
                    </div>
                    <div className="footer">
                        <button
                            style={{ opacity: showClear ? '0' : '1' }}
                            className="clear"
                            onClick={clearAll}
                            disabled={showClear}
                        >
                            {formatMessage({ id: 'Clear', defaultMessage: 'Clear' })}
                        </button>
                        <button className="show" onClick={saveFilters}>
                            {formatMessage({ id: 'Save', defaultMessage: 'Save' })}
                        </button>
                    </div>
                </div>
            </div>
        </Backdrop>
    )
}

export default MoreFilter
